import { createContext, useContext, useState, type ReactNode } from 'react';

type Product = {
  id: string | number;
  icon: string;
  name: string;
  desc: string;
  basePrice: number;
};

type UserInfo = {
  name: string;
  age: number;
  gender: string;
};

type Order = {
  product: Product | null;
  totalPrice: number;
  extras: string[];
  /** ml amount per product UUID (or ingredient name fallback) for custom-mix orders */
  customMix: Record<string, number>;
  /** Which flow the user chose from MenuTypePage */
  orderMode: 'main' | 'custom' | 'ai';
  /** Collected from the intake form before the menu */
  user: UserInfo | null;
};

type OrderContextValue = {
  order: Order;
  updateOrder: (updates: Partial<Order>) => void;
  resetOrder: () => void;
};

const createInitialOrder = (): Order => ({
  product: null,
  extras: [],
  totalPrice: 0,
  customMix: {},
  orderMode: 'main',
  user: null,
});

const OrderContext = createContext<OrderContextValue | undefined>(undefined);

export const OrderProvider = ({ children }: { children: ReactNode; }) => {
  const [order, setOrder] = useState<Order>(createInitialOrder());

  const updateOrder = (updates: Partial<Order>) => {
    setOrder((prev) => ({ ...prev, ...updates }));
  };

  const resetOrder = () => {
    setOrder(createInitialOrder());
  };

  return (
    <OrderContext.Provider value={{ order, updateOrder, resetOrder }}>
      {children}
    </OrderContext.Provider>
  );
};

export const useOrder = (): OrderContextValue => {
  const ctx = useContext(OrderContext);
  if (!ctx) {
    throw new Error('useOrder must be used within OrderProvider');
  }
  return ctx;
};